import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CalendarClock, ChevronDown, ChevronRight, FileText, ListChecks } from 'lucide-react';
import type { ClientProject } from '../../lib/clientsApi';
import { listMeetingNotes, type MeetingNote } from '../../lib/meetingNotesApi';
import { listTasks, type Task } from '../../lib/tasksApi';
import { formatCurrency, formatDate } from '../../lib/format';
import { TaskListRow, taskRow } from '../tasks/TaskListRow';

const STATUS_STYLES: Record<string, string> = {
  PLANNING: 'bg-gray-100 text-gray-700',
  ACTIVE: 'bg-brand-600 text-white',
  COMPLETED: 'bg-green-100 text-green-700',
  ON_HOLD: 'bg-amber-100 text-amber-700',
  CANCELLED: 'bg-red-100 text-red-700',
};

// Newest first.
function byDateDesc<T extends { date?: string | null; updatedAt?: string | null }>(a: T, b: T) {
  const da = new Date(a.date ?? a.updatedAt ?? 0).getTime();
  const db = new Date(b.date ?? b.updatedAt ?? 0).getTime();
  return db - da;
}

/** Truncate a possibly-long note body to a single readable preview line. */
function preview(text: string | null, max = 160): string {
  if (!text) return '';
  const flat = text.replace(/\s+/g, ' ').trim();
  return flat.length > max ? `${flat.slice(0, max - 1)}…` : flat;
}

function NotesBlock({ projectId }: { projectId: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['client-project-notes', projectId],
    queryFn: () => listMeetingNotes({ projectId }),
  });

  if (isLoading) return <p className="text-xs text-gray-400">Se încarcă notițele…</p>;
  if (isError) return <p className="text-xs text-red-500">Notițele nu au putut fi încărcate.</p>;

  const notes: MeetingNote[] = [...(data ?? [])].sort(byDateDesc).slice(0, 3);
  if (notes.length === 0) {
    return <p className="text-xs text-gray-400">Nicio notiță de ședință pentru acest proiect.</p>;
  }

  return (
    <ul className="space-y-2">
      {notes.map((n) => (
        <li key={n.id} className="rounded-md border border-gray-100 bg-gray-50 p-2">
          <div className="flex items-center justify-between gap-2">
            <span className="truncate text-xs font-medium text-gray-800">{n.title}</span>
            <span className="shrink-0 text-[11px] text-gray-400">{formatDate(n.date)}</span>
          </div>
          {(n.summary || n.decisions || n.content) && (
            <p className="mt-1 text-xs leading-relaxed text-gray-600">
              {preview(n.summary ?? n.decisions ?? n.content)}
            </p>
          )}
        </li>
      ))}
    </ul>
  );
}

function RecentTasksBlock({ projectId }: { projectId: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['client-project-recent-tasks', projectId],
    queryFn: () => listTasks({ projectId, sort: 'updatedAt', order: 'desc', pageSize: 5 }),
  });

  if (isLoading) return <p className="text-xs text-gray-400">Se încarcă activitatea…</p>;
  if (isError) return <p className="text-xs text-red-500">Activitatea nu a putut fi încărcată.</p>;

  const tasks: Task[] = (data?.tasks ?? []).slice(0, 5);
  if (tasks.length === 0) {
    return <p className="text-xs text-gray-400">Nicio activitate recentă.</p>;
  }

  return (
    <ul className="overflow-hidden rounded-md border border-gray-200">
      {tasks.map((t) => (
        <TaskListRow key={t.id} task={taskRow(t)} />
      ))}
    </ul>
  );
}

/**
 * One client project rendered as an expandable card (CLE-274). The header shows
 * project name, status, dates and budget; expanding reveals the project's
 * meeting notes and recent task activity (lazily fetched). Tasks link to the
 * canonical task-detail route.
 */
export function ClientProjectCard({ project }: { project: ClientProject }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-lg border border-gray-200">
      <div className="flex items-center gap-3 px-3 py-2.5">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={open ? 'Restrânge proiectul' : 'Extinde proiectul'}
          className="shrink-0 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        <div className="min-w-0 flex-1">
          <Link
            to={`/projects/${project.id}`}
            className="font-medium text-brand-700 hover:underline"
          >
            {project.name}
          </Link>
          {project.code && <span className="ml-2 text-xs text-gray-400">{project.code}</span>}
        </div>
        <span
          className={`hidden shrink-0 rounded-full px-2 py-0.5 text-xs font-medium sm:inline-block ${
            STATUS_STYLES[project.status] ?? 'bg-gray-100 text-gray-600'
          }`}
        >
          {project.status}
        </span>
        <span className="hidden shrink-0 text-xs text-gray-500 md:inline">
          {formatDate(project.startDate)} → {formatDate(project.endDate)}
        </span>
        <span className="shrink-0 text-sm text-gray-700">
          {project.budget != null ? formatCurrency(project.budget, project.budgetCurrency) : '—'}
        </span>
      </div>

      {open && (
        <div className="grid gap-4 border-t border-gray-100 px-4 py-3 sm:grid-cols-2">
          <section>
            <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
              <FileText size={14} /> Notițe proiect
            </h4>
            <NotesBlock projectId={project.id} />
          </section>
          <section>
            <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
              <ListChecks size={14} /> Activitate recentă
            </h4>
            <RecentTasksBlock projectId={project.id} />
            <Link
              to={`/projects/${project.id}`}
              className="mt-2 inline-flex items-center gap-1 text-xs text-brand-600 hover:underline"
            >
              <CalendarClock size={12} /> Vezi toate task-urile proiectului →
            </Link>
          </section>
        </div>
      )}
    </div>
  );
}
